/**
 * The partner's engagement history — every dispatched assignment locked
 * to them, newest first, with the review (four scores + the reviewer's
 * comments) once one is submitted — plus the review aggregates over all
 * engagements in the header.
 */

import { Link } from 'react-router-dom';

import { Anchor, Badge, Group, Pagination, Stack, Text, Title, Tooltip } from '@mantine/core';

import {
  NP_REVIEW_DIMENSIONS,
  type NpEngagement,
  type NpEngagements,
} from '@/types/network-partner';

interface PartnerEngagementsPanelProps {
  data: NpEngagements | null;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}

export function PartnerEngagementsPanel({
  data,
  page,
  pageSize,
  onPageChange,
}: Readonly<PartnerEngagementsPanelProps>) {
  const pages = data ? Math.ceil(data.total / pageSize) : 0;
  const engagements = data?.engagements ?? [];
  return (
    <Stack gap="sm">
      <Group justify="space-between">
        <Title order={4}>Engagements</Title>
        {data && data.review_count > 0 && <ReviewAggregates data={data} />}
      </Group>
      {engagements.length === 0 ? (
        <Text size="sm" c="dimmed">
          No dispatched engagements yet.
        </Text>
      ) : (
        <Stack gap={6}>
          {engagements.map((engagement) => (
            <EngagementRow key={engagement.assignment_id} engagement={engagement} />
          ))}
        </Stack>
      )}
      {pages > 1 && (
        <Group justify="center">
          <Pagination value={page} onChange={onPageChange} total={pages} size="sm" />
        </Group>
      )}
    </Stack>
  );
}

const AGGREGATE_BADGES: { label: string; short: string; key: keyof NpEngagements }[] = [
  { label: 'Timeliness', short: 'T', key: 'avg_timeliness' },
  { label: 'Quality', short: 'Q', key: 'avg_quality' },
  { label: 'Ease of interaction', short: 'E', key: 'avg_ease_of_interaction' },
  { label: 'Experience with GTI', short: 'G', key: 'avg_gti_experience' },
];

function ReviewAggregates({ data }: Readonly<{ data: NpEngagements }>) {
  return (
    <Group gap="xs">
      <Badge variant="light" color="teal" data-testid="np-avg-overall">
        Overall {data.avg_overall}
      </Badge>
      {AGGREGATE_BADGES.map((b) => (
        <Tooltip key={b.key} label={b.label}>
          <Badge variant="light">
            {b.short} {data[b.key] as number | null}
          </Badge>
        </Tooltip>
      ))}
      <Text size="xs" c="dimmed">
        {data.review_count} review{data.review_count === 1 ? '' : 's'}
      </Text>
    </Group>
  );
}

function engagementMeta(engagement: NpEngagement): string {
  const parts = [`${engagement.items_count} item${engagement.items_count === 1 ? '' : 's'}`];
  if (engagement.np_fee != null) {
    parts.push(`fee ${engagement.np_currency ?? ''} ${engagement.np_fee}`);
  }
  if (engagement.dispatched_at) {
    parts.push(`dispatched ${new Date(engagement.dispatched_at).toLocaleDateString()}`);
  }
  return parts.join(' · ');
}

function EngagementRow({ engagement }: Readonly<{ engagement: NpEngagement }>) {
  const id = engagement.assignment_id;
  return (
    <Group
      justify="space-between"
      wrap="nowrap"
      align="flex-start"
      data-testid={`np-engagement-${id}`}
    >
      <Stack gap={0} style={{ minWidth: 0 }}>
        <Anchor component={Link} to={`/reports/${engagement.report_id}`} size="sm">
          Report {engagement.report_id.slice(0, 8)}…
        </Anchor>
        <Text size="xs" c="dimmed">
          {engagementMeta(engagement)}
        </Text>
        {engagement.investigator_notes && (
          <Tooltip label={engagement.investigator_notes} multiline maw={420} withArrow>
            <Text
              size="xs"
              c="dimmed"
              fs="italic"
              lineClamp={1}
              data-testid={`engagement-notes-${id}`}
            >
              “{engagement.investigator_notes}”
            </Text>
          </Tooltip>
        )}
        {engagement.review?.comments && (
          <Text size="xs" fs="italic" data-testid={`engagement-review-comments-${id}`}>
            Reviewer: “{engagement.review.comments}”
          </Text>
        )}
      </Stack>
      <Group gap="xs" wrap="nowrap" align="flex-start">
        {engagement.completed_at ? (
          <Badge size="sm" variant="light" color="green">
            {engagement.outcome ?? 'completed'}
          </Badge>
        ) : (
          <Badge size="sm" variant="light" color="blue">
            In progress
          </Badge>
        )}
        <EngagementReview engagement={engagement} />
      </Group>
    </Group>
  );
}

function EngagementReview({ engagement }: Readonly<{ engagement: NpEngagement }>) {
  const review = engagement.review;
  if (review) {
    return (
      <Stack gap={2} align="flex-end">
        <Badge size="sm" variant="light" color="teal">
          ★ {review.overall}
        </Badge>
        <Text
          size="xs"
          c="dimmed"
          style={{ whiteSpace: 'nowrap' }}
          data-testid={`engagement-scores-${engagement.assignment_id}`}
        >
          {NP_REVIEW_DIMENSIONS.map((d) => `${d.short} ${review[d.key] ?? '–'}`).join(' · ')}
        </Text>
      </Stack>
    );
  }
  if (engagement.completed_at) {
    return (
      <Text size="xs" c="dimmed">
        Not reviewed
      </Text>
    );
  }
  return null;
}
